import React, { useState, type ReactNode } from "react";

import cx from "@hsl/core/utils/cx";

import CustomScroller from "../CustomScroller";
import { useAccordionContext } from "../FundCard/components/FundCardAccordions";
import { ChevronDownSlim } from "../icons";
import Tab, { type Tab as ITab } from "./components/Tab";
import { TabsProvider } from "./context";

export type TabTypes = "default" | "pill" | "text";

export interface TabsProps {
    column?: boolean;
    containerClassName?: string;
    content?: (ReactNode | (() => ReactNode))[];
    contentContainerClassName?: string;
    scrollerClassName?: string;
    iconClassName?: string;
    tabClassName?: string;
    tabs: ITab[];
    type?: TabTypes;
    withBorder?: boolean;
    activeIndex?: number;
    withArrows?: boolean;
    children?: ReactNode;
    onChange?(activeIndex: number): void;
    className?: string;
}

const Tabs = ({
    column = false,
    containerClassName,
    content = [],
    contentContainerClassName,
    scrollerClassName,
    iconClassName,
    type = "default",
    tabClassName,
    tabs = [],
    withBorder = false,
    activeIndex,
    withArrows = false,
    children,
    onChange,
    className,
}: TabsProps) => {
    const context = useAccordionContext();
    const activeTabIndexFromContext =
        context && tabs.findIndex((tab) => tab.key === context);

    const [activeTabIndex, setActiveTabIndex] = useState(activeIndex ?? 0);

    const handleTabClick = (index: number) => {
        setActiveTabIndex(index);
        !!onChange && onChange(index);
    };

    const handleNextClick = () => {
        if (activeTabIndex < tabs.length - 1) {
            setActiveTabIndex(activeTabIndex + 1);
        }
    };

    const handlePrevClick = () => {
        if (activeTabIndex > 0) {
            setActiveTabIndex(activeTabIndex - 1);
        }
    };

    if (activeTabIndex >= tabs.length) {
        setActiveTabIndex(0);
    }

    return (
        <TabsProvider>
            {!context && (
                <div className={cx("relative", className)}>
                    <CustomScroller className={scrollerClassName}>
                        <div
                            className={cx(
                                withBorder && "border-platinum border-b",
                                containerClassName,
                            )}
                        >
                            <nav
                                className={cx(
                                    "-mb-px flex",
                                    type === "pill" && "pb-2",
                                    column ? "flex-col" : "flex-row",
                                )}
                                aria-label="Tabs"
                            >
                                {tabs.map((tab, i) => {
                                    return (
                                        <Tab
                                            tab={tab}
                                            key={tab.key}
                                            current={activeTabIndex === i}
                                            type={type}
                                            className={cx(
                                                tabClassName,
                                                "z-[35] [&>span]:w-max",
                                            )}
                                            iconClassName={iconClassName}
                                            onClick={() => handleTabClick(i)}
                                            disabled={tabs.length === 1}
                                        />
                                    );
                                })}
                                {children && (
                                    <div className="grid flex-1 place-items-center">
                                        {children}
                                    </div>
                                )}
                            </nav>
                        </div>
                    </CustomScroller>
                </div>
            )}
            {/* CONTENT */}
            {content.length > 0 && (
                <div className={contentContainerClassName}>
                    <div
                        className={cx(
                            withArrows &&
                                "flex items-center justify-evenly gap-x-4 sm:gap-x-6 lg:gap-x-8",
                        )}
                    >
                        {withArrows && (
                            <ArrowButton
                                left
                                disabled={activeTabIndex === 0}
                                onClick={handlePrevClick}
                            />
                        )}
                        <div className={cx(withArrows && "flex-1")}>
                            {content.map((elem, i) => (
                                <div
                                    key={`${i}-content`}
                                    className={cx(
                                        (context
                                            ? i !==
                                              (activeTabIndexFromContext ?? 0)
                                            : i !== activeTabIndex) && "hidden",
                                    )}
                                >
                                    {typeof elem === "function" ? elem() : elem}
                                </div>
                            ))}
                        </div>
                        {withArrows && (
                            <ArrowButton
                                right
                                disabled={activeTabIndex === tabs.length - 1}
                                onClick={handleNextClick}
                            />
                        )}
                    </div>
                </div>
            )}
        </TabsProvider>
    );
};

export default Tabs;

const ArrowButton = ({
    right,
    left,
    disabled,
    onClick,
}: {
    right?: boolean;
    left?: boolean;
    disabled?: boolean;
    onClick?: () => void;
}) => {
    if (!right && !left) {
        return null;
    }
    const handleClick = () => {
        if (!disabled && onClick) {
            onClick();
        }
    };
    return (
        <ChevronDownSlim
            className={cx(
                "h-[75px] cursor-pointer transition",
                right && "rotate-180 transform",
                disabled ? "opacity-25" : "hover:opacity-75",
            )}
            onClick={handleClick}
        />
    );
};
